import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  CreditCard,
  Users,
  Mail,
  Crown,
  UserPlus,
  LogOut,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'
import { formatDate } from '@/lib/utils'
import api from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import type { Subscription, Household } from '@/types'

export function ProfilePage() {
  const user = useAuthStore((state) => state.user)
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [household, setHousehold] = useState<Household | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      try {
        const [subResponse, householdResponse] = await Promise.all([
          api.get('/subscription/status'),
          api.get('/household').catch(() => ({ data: null })),
        ])
        setSubscription(subResponse.data)
        setHousehold(householdResponse.data)
      } catch (error) {
        console.error('Failed to fetch profile data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleSubscribe = async (billingCycle: 'monthly' | 'yearly') => {
    try {
      const response = await api.post('/subscription/create-checkout', {
        billing_cycle: billingCycle,
      })
      // Redirect to Stripe checkout page
      if (response.data.url) {
        window.location.href = response.data.url
      }
    } catch (error) {
      console.error('Failed to start checkout:', error)
    }
  }

  const handleManageBilling = async () => {
    try {
      const response = await api.post('/subscription/portal')
      window.location.href = response.data.url
    } catch (error) {
      console.error('Failed to open billing portal:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const getStatusBadge = () => {
    if (!subscription) return <Badge variant="secondary">Free</Badge>

    switch (subscription.status) {
      case 'active':
        return (
          <Badge variant="success" className="bg-success text-white">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        )
      case 'trialing':
        return (
          <Badge className="bg-primary-500 text-white">
            <Crown className="mr-1 h-3 w-3" />
            Trial
          </Badge>
        )
      case 'past_due':
        return (
          <Badge variant="destructive">
            <AlertCircle className="mr-1 h-3 w-3" />
            Past Due
          </Badge>
        )
      case 'canceled':
        return <Badge variant="secondary">Canceled</Badge>
      default:
        return <Badge variant="secondary">Inactive</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Profile
      </h1>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarFallback className="bg-primary-100 text-primary-600 text-xl dark:bg-primary-900/30 dark:text-primary-400">
                  {user?.email.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {user?.email}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  {user?.email_verified ? (
                    <Badge variant="secondary" className="text-success">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-warning">
                      <AlertCircle className="mr-1 h-3 w-3" />
                      Unverified
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Auth Method</span>
                <span className="text-gray-900 dark:text-gray-100">
                  {user?.google_sub
                    ? 'Google'
                    : user?.apple_sub
                    ? 'Apple'
                    : 'Email'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Subscription
              </CardTitle>
              {getStatusBadge()}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {subscription?.status === 'active' || subscription?.status === 'trialing' ? (
              <>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Plan</span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {subscription.tier || 'Premium'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Billing</span>
                    <span className="capitalize text-gray-900 dark:text-gray-100">
                      {subscription.billing_cycle || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Platform</span>
                    <span className="capitalize text-gray-900 dark:text-gray-100">
                      {subscription.platform || 'N/A'}
                    </span>
                  </div>
                  {subscription.current_period_end && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {subscription.cancel_at_period_end ? 'Expires' : 'Renews'}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {formatDate(subscription.current_period_end)}
                      </span>
                    </div>
                  )}
                </div>

                {subscription.platform === 'stripe' && (
                  <Button variant="outline" className="w-full" onClick={handleManageBilling}>
                    Manage Billing
                  </Button>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Upgrade to Premium to unlock all features including unlimited
                  accounts, transaction history, and more.
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Button
                    variant="outline"
                    onClick={() => handleSubscribe('monthly')}
                  >
                    $9.99/mo
                  </Button>
                  <Button onClick={() => handleSubscribe('yearly')}>
                    $99.99/yr
                    <Badge variant="secondary" className="ml-2">
                      Save 17%
                    </Badge>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Household */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Household
            </CardTitle>
            <CardDescription>
              Share your financial data with family members
            </CardDescription>
          </CardHeader>
          <CardContent>
            {household ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {household.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {household.members.length} member
                      {household.members.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Invite
                    </Button>
                    <Button variant="outline" size="sm" className="text-danger">
                      <LogOut className="mr-2 h-4 w-4" />
                      Leave
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  {household.members.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {member.avatar || member.email.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {member.nickname || member.email}
                          </p>
                          {member.nickname && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {member.email}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge variant="secondary" className="capitalize">
                        {member.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Users className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  No household yet
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Create a household to share your financial data with family
                  members.
                </p>
                <Button className="mt-4">
                  <Users className="mr-2 h-4 w-4" />
                  Create Household
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
